import { z } from 'zod';
import { MAX_DRAWING_LAYERS } from './drawing-layer.js';

export const aiDrawingLayerIdSchema = z.string().regex(/^[a-z0-9_-]{1,64}$/i);
const name = z.string().trim().min(1).max(120);
export const aiDrawingLayerActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('create_drawing_layer'), layerId: aiDrawingLayerIdSchema, name }).strict(),
  z.object({ type: z.literal('reorder_drawing_layers'), layerIds: z.array(aiDrawingLayerIdSchema).min(1).max(MAX_DRAWING_LAYERS).refine((ids) => new Set(ids).size === ids.length) }).strict(),
  z.object({ type: z.literal('rename_drawing_layer'), layerId: aiDrawingLayerIdSchema, name }).strict(),
  z.object({ type: z.literal('move_drawings_to_layer'), layerId: aiDrawingLayerIdSchema, drawingIds: z.array(z.string().min(1).max(128)).min(1).max(10000) }).strict(),
]);
export type AiDrawingLayerAction = z.infer<typeof aiDrawingLayerActionSchema>;
export const aiDrawingLayerContextSchema = z.array(z.object({
  id: aiDrawingLayerIdSchema, name, visible: z.boolean(), position: z.number().int().nonnegative(),
})).max(MAX_DRAWING_LAYERS).optional();

export function drawingLayerPlanError(actions: import('./ai-map.js').AiMapAction[], layers: Array<{ id: string }>, drawingIds: ReadonlySet<string>, limit = MAX_DRAWING_LAYERS): string | null {
  const layerIds = new Set(layers.map((layer) => layer.id));
  const availableDrawings = new Set(drawingIds);
  for (const action of actions) {
    if (action.type === 'clear') availableDrawings.clear();
    if (action.type === 'delete') action.drawingIds.forEach((id) => availableDrawings.delete(id));
    if (action.type === 'create_drawing_layer') {
      if (layerIds.has(action.layerId)) return 'The drawing layer ID already exists.';
      if (layerIds.size >= limit) return 'The drawing layer limit has been reached.';
      layerIds.add(action.layerId);
    }
    if (action.type === 'reorder_drawing_layers' && (action.layerIds.length !== layerIds.size || action.layerIds.some((id) => !layerIds.has(id)))) return 'Layer order must include every drawing layer exactly once, from bottom to top.';
    if (action.type === 'rename_drawing_layer' || action.type === 'move_drawings_to_layer') {
      if (!layerIds.has(action.layerId)) return 'The drawing layer does not exist. Create it before using it.';
      if (action.type === 'move_drawings_to_layer' && action.drawingIds.some((id) => !availableDrawings.has(id))) return 'A drawing to move does not exist.';
    }
  }
  return null;
}
